import React, { memo, ReactElement, useState, useCallback, useContext, useMemo } from "react";
import { BottomSheet, BottomSheetChildrenProps, useEffectOnce, getCloudinaryUrl } from "@swiggy-private/core-ui";
import {
    FallbackAlertPayButton,
    FallbackAlertNoPaymentMethod,
    FallbackAlertPaymentMethod,
    FallbackAlertSplitPay,
    PaymentFallbacksApiResponse,
    PaymentMethodDef,
    FallbackAlertTryOtherPaymentMethods,
    PaymentMethodIcon,
} from "@swiggy-private/payment-ui";
import { PaymentPayContext } from "@payment-client/contexts/PaymentPayContext";

//analytics
import {
    impressionPaymentFailureHalfCard,
    clickRecommendedPaymentMethod,
    clickLastUsedPaymentMethod,
    clickMorePaymentMethodsHalfCard,
    clickProceedToPayHalfCard,
    clickTryOtherPayment,
    clickToggleSwiggyPay,
    impressionLastUsedPaymentMethod,
    impressionRecommendedPaymentMethod,
    impressionTechnicalFailureHalfCard,
    impressionMorePaymentMethodsHalfCard,
} from "@payment-client/analytics/events/PaymentFallbacks";
import { AnalyticsContext } from "@swiggy-private/checkout-core";
import { PAYMENT_FALLBACK_ALERT_TEST_IDS } from "@swiggy-private/payment-test-ids";
import { generatePaymentMethodId } from "@payment-client/helpers/PaymentHelper";

import styles from "./FallbackAlert.v4.scss";

interface FallbackAlertProps {
    hideOnOverlayClick?: boolean;
    onClose: VoidFunction;
    onCloseAndReload: VoidFunction;
    fallbackApiMethods: PaymentFallbacksApiResponse;
    payAction: (paymentMethodData: PaymentMethodDef, isSplitPaySelected: boolean) => void;
}

interface AlertContentProps {
    fallbackApiMethods: PaymentFallbacksApiResponse;
    onClose: VoidFunction;
    payAction: (paymentMethodData: PaymentMethodDef, isSplitPaySelected: boolean) => void;
}

const TRY_OTHER_PAYMENT_METHODS = "TRY ANOTHER PAYMENT METHOD";

const FallbackAlert = (props: FallbackAlertProps): ReactElement | null => {
    const { hideOnOverlayClick = true, onCloseAndReload, ..._props } = props;
    const { fallbackApiMethods } = _props;
    const { failureReason = "" } = fallbackApiMethods;
    const { impressionEvent } = useContext(AnalyticsContext);

    useEffectOnce(() => {
        impressionPaymentFailureHalfCard(impressionEvent, failureReason);
    });
    return (
        <BottomSheet
            hideOnOverlayClick={hideOnOverlayClick}
            onClose={onCloseAndReload}
            closeButtonDataTestId={PAYMENT_FALLBACK_ALERT_TEST_IDS.CLOSE_ICON}
        >
            {(bProps: BottomSheetChildrenProps): React.ReactElement => (
                <AlertContentComponent {...bProps} {..._props} />
            )}
        </BottomSheet>
    );
};

export default memo(FallbackAlert);

const ICON_PAYMENT_ALERT_V4 = "PaymentLogos/paymentIcons/paylater/paymenterror";

const AlertContentComponent = (props: BottomSheetChildrenProps & AlertContentProps): ReactElement | null => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const [selectedPaymentMethod, setSelectedPaymentMethod] = useState<any>();
    const [isSplitPaySelected, setIsSplitPaySelected] = useState(false);
    const { clickEvent, impressionEvent } = useContext(AnalyticsContext);
    const { paymentPageOfferEnabled } = useContext(PaymentPayContext);

    const failureIconUrl = getCloudinaryUrl(ICON_PAYMENT_ALERT_V4, {
        trim: true,
    });

    const { fallbackApiMethods, payAction } = props;
    const { failureTitle, failureReason } = fallbackApiMethods;

    const orderAmount = selectedPaymentMethod?.payButtonEntity.orderAmount;
    const numberOfItems = fallbackApiMethods.numberOfItems;
    const paymentAmount = useMemo(() => {
        return selectedPaymentMethod?.payableAmount ?? orderAmount;
    }, [orderAmount, selectedPaymentMethod?.payableAmount]);

    const onSelectPaymentMethod = useCallback(
        (methodData: PaymentMethodDef) => {
            if (methodData.lastUsedPm) {
                clickLastUsedPaymentMethod(methodData.groupName, methodData.couponApplicable, clickEvent);
            } else {
                clickRecommendedPaymentMethod(methodData.groupName, methodData.couponApplicable, clickEvent);
            }

            setSelectedPaymentMethod(methodData);
            setIsSplitPaySelected(methodData.splitPaySelected ?? false);
        },
        [clickEvent],
    );

    const handlePayClick = useCallback(() => {
        if (!selectedPaymentMethod) return;

        clickProceedToPayHalfCard(selectedPaymentMethod.groupName, selectedPaymentMethod.lastUsedPm, clickEvent);
        payAction(selectedPaymentMethod, isSplitPaySelected);
        props.onClose();
    }, [selectedPaymentMethod, payAction, props, isSplitPaySelected, clickEvent]);

    const handleUseOtherPMClick = useCallback(() => {
        clickMorePaymentMethodsHalfCard(clickEvent);
        props.close();
    }, [props, clickEvent]);

    const handleTryOtherPMClick = useCallback(() => {
        clickTryOtherPayment(clickEvent);
        props.close();
    }, [props, clickEvent]);

    const handleSplitPayChange = useCallback(() => {
        if (selectedPaymentMethod.splitPayToggleEditable) {
            clickToggleSwiggyPay(!isSplitPaySelected, clickEvent);
            setIsSplitPaySelected(!isSplitPaySelected);
        }
    }, [selectedPaymentMethod, isSplitPaySelected, clickEvent]);

    const onPaymentMethodImpression = useCallback(
        (methodData: PaymentMethodDef) => {
            if (methodData.lastUsedPm) {
                impressionLastUsedPaymentMethod(methodData.groupName, methodData.couponApplicable, impressionEvent);
            } else {
                impressionRecommendedPaymentMethod(methodData.groupName, methodData.couponApplicable, impressionEvent);
            }
        },
        [impressionEvent],
    );

    const onNoPaymentMethodsImpression = useCallback(() => {
        impressionTechnicalFailureHalfCard(impressionEvent);
    }, [impressionEvent]);

    const onUseOtherPaymentsImpression = useCallback(() => {
        impressionMorePaymentMethodsHalfCard(impressionEvent);
    }, [impressionEvent]);

    const getItemPriceText = useCallback(() => {
        return `${paymentAmount} | ${numberOfItems} ${numberOfItems > 1 ? "Items" : "Item"}`;
    }, [paymentAmount, numberOfItems]);

    useEffectOnce(() => {
        if (fallbackApiMethods.paymentMethods.length > 0) {
            setSelectedPaymentMethod(fallbackApiMethods.paymentMethods[0]);
            setIsSplitPaySelected(fallbackApiMethods.paymentMethods[0].splitPaySelected ?? false);
        }
    });

    return (
        <div>
            <div className={styles.container}>
                <div className={styles.failureTitleContainer} data-testid={PAYMENT_FALLBACK_ALERT_TEST_IDS.TITLE}>
                    <PaymentMethodIcon src={failureIconUrl} parentClassName={styles.failureIcon} />
                    <div className={styles.failureTitle}>{failureReason || failureTitle}</div>
                </div>

                {fallbackApiMethods.paymentMethods.length > 0 && (
                    <>
                        <div
                            className={styles.paymentMethodsContainer}
                            data-testid={PAYMENT_FALLBACK_ALERT_TEST_IDS.PAYMENT_METHODS_CONTAINER}
                        >
                            <div className={styles.paymentMethods}>
                                {fallbackApiMethods.splitpay && !paymentPageOfferEnabled && (
                                    <>
                                        <FallbackAlertSplitPay
                                            isSelected={isSplitPaySelected}
                                            onClick={handleSplitPayChange}
                                            splitPayData={fallbackApiMethods.splitpay}
                                            inlineMessage={selectedPaymentMethod?.splitPayInlineMessage ?? ""}
                                            isDisabled={!selectedPaymentMethod?.splitPayToggleEditable}
                                            orderAmount={selectedPaymentMethod?.payButtonEntity.orderAmount}
                                        />
                                        <div className={styles.separator} />
                                    </>
                                )}

                                {fallbackApiMethods.paymentMethods.map(paymentMethodData => {
                                    const id = generatePaymentMethodId(paymentMethodData);
                                    return (
                                        <>
                                            <FallbackAlertPaymentMethod
                                                id={id}
                                                paymentMethodData={paymentMethodData}
                                                onSelectPaymentMethod={onSelectPaymentMethod}
                                                onImpression={onPaymentMethodImpression}
                                                isSelected={
                                                    selectedPaymentMethod
                                                        ? generatePaymentMethodId(selectedPaymentMethod) === id
                                                        : false
                                                }
                                            />
                                            <div className={styles.separator} />
                                        </>
                                    );
                                })}
                            </div>

                            <FallbackAlertTryOtherPaymentMethods
                                onClick={handleUseOtherPMClick}
                                title={fallbackApiMethods.otherPaymentMethodBanner.title}
                                onImpression={onUseOtherPaymentsImpression}
                            />
                        </div>
                        {fallbackApiMethods.splitpay && paymentPageOfferEnabled && (
                            <div className={styles.paymentMethodsContainer}>
                                <FallbackAlertSplitPay
                                    isSelected={isSplitPaySelected}
                                    onClick={handleSplitPayChange}
                                    paymentPageOfferEnabled={paymentPageOfferEnabled}
                                    splitPayData={fallbackApiMethods.splitpay}
                                    inlineMessage={selectedPaymentMethod?.splitPayInlineMessage ?? ""}
                                    isDisabled={!selectedPaymentMethod?.splitPayToggleEditable}
                                    orderAmount={selectedPaymentMethod?.payButtonEntity.orderAmount}
                                />
                            </div>
                        )}
                        <FallbackAlertPayButton onClick={handlePayClick}>
                            <div className={styles.payButtonContent}>
                                <div className={styles.payButtonContentLeft}>{getItemPriceText()}</div>
                                <div className={styles.payButtonContentRight}>
                                    <div className={styles.payButtonContentRightText}>
                                        {selectedPaymentMethod?.payButtonEntity?.inlineMessage ?? ""}
                                    </div>
                                </div>
                            </div>
                        </FallbackAlertPayButton>
                    </>
                )}
                {fallbackApiMethods.paymentMethods.length === 0 && (
                    <FallbackAlertNoPaymentMethod
                        onClick={handleTryOtherPMClick}
                        onImpression={onNoPaymentMethodsImpression}
                        title={fallbackApiMethods?.otherPaymentMethodBanner?.title || TRY_OTHER_PAYMENT_METHODS}
                    />
                )}
            </div>
        </div>
    );
};
